import { useState, useEffect, useRef, useMemo } from 'react';
import { fetchArtifact as fetchArtifactUtil } from '../../utils/widgetApi';
import DOMPurify from 'dompurify';
import { getSvgThemeCSS, getThemeVarsCSS, getHtmlBaseCSS } from './visualizeTheme';
import ComponentRenderer from './native/ComponentRenderer';

function VisualizeSkeleton() {
    const skeletonClass = "animate-pulse bg-gray-200 dark:bg-gray-700 rounded";
    return (
        <div className="overflow-hidden">
            <div className={`h-[300px] w-full ${skeletonClass}`} />
        </div>
    );
}

function VisualizeError({ error, onRetry }) {
    return (
        <div className="text-center">
            <p className="text-sm text-red-600 dark:text-red-400 mb-2">
                Failed to load visualization
            </p>
            {error && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{error}</p>
            )}
            {onRetry && (
                <button
                    onClick={onRetry}
                    className="text-xs px-3 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                    Retry
                </button>
            )}
        </div>
    );
}

function useDarkMode() {
    const [isDark, setIsDark] = useState(() =>
        document.documentElement.classList.contains('dark') ||
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class', 'data-mode', 'data-theme'],
        });

        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setIsDark(e.matches);
        mq.addEventListener('change', handler);

        return () => {
            observer.disconnect();
            mq.removeEventListener('change', handler);
        };
    }, []);

    return isDark;
}

function SvgRenderer({ code }) {
    const isDark = useDarkMode();

    const sanitized = useMemo(() => {
        return DOMPurify.sanitize(code, {
            USE_PROFILES: { svg: true, svgFilters: true },
            ADD_TAGS: ['use', 'marker', 'defs', 'clipPath', 'linearGradient', 'radialGradient', 'stop', 'pattern', 'style'],
            ADD_ATTR: [
                'viewBox', 'xmlns', 'fill', 'stroke', 'stroke-width', 'stroke-linecap',
                'stroke-linejoin', 'stroke-dasharray', 'opacity', 'transform', 'text-anchor',
                'dominant-baseline', 'font-size', 'font-weight', 'font-family',
                'marker-end', 'marker-start', 'marker-mid', 'refX', 'refY',
                'markerWidth', 'markerHeight', 'orient', 'rx', 'ry',
                'd', 'cx', 'cy', 'r', 'x', 'y', 'x1', 'y1', 'x2', 'y2',
                'width', 'height', 'points', 'offset', 'stop-color', 'stop-opacity',
                'gradientUnits', 'gradientTransform', 'patternUnits', 'patternTransform',
                'class', 'id', 'dx', 'dy', 'onclick',
            ],
        });
    }, [code]);

    const themeCSS = useMemo(() => getSvgThemeCSS(isDark), [isDark]);

    return (
        <div className="visualize-svg-container w-full">
            <style>{themeCSS}</style>
            <div dangerouslySetInnerHTML={{ __html: sanitized }} />
        </div>
    );
}

function HtmlRenderer({ code, title }) {
    const iframeRef = useRef(null);
    const isDark = useDarkMode();
    const [iframeHeight, setIframeHeight] = useState(400);

    const srcdoc = useMemo(() => {
        const themeVars = getThemeVarsCSS(isDark);
        const baseCSS = getHtmlBaseCSS();

        return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
${themeVars}
${baseCSS}
</style>
</head>
<body>${code}<script>
function sendPrompt(text) {
    window.parent.postMessage({ type: 'sendPrompt', text: text }, '*');
}
new ResizeObserver(function() {
    var h = document.documentElement.scrollHeight;
    window.parent.postMessage({ type: 'resize', height: h }, '*');
}).observe(document.body);
window.parent.postMessage({ type: 'resize', height: document.documentElement.scrollHeight }, '*');
</script></body>
</html>`;
    }, [code, isDark]);

    useEffect(() => {
        const handler = (event) => {
            if (event.source !== iframeRef.current?.contentWindow) return;
            const data = event.data;
            if (data?.type === 'resize' && typeof data.height === 'number') {
                setIframeHeight(Math.min(Math.max(data.height, 100), 2000));
            }
            if (data?.type === 'sendPrompt' && typeof data.text === 'string') {
                const input = document.querySelector('textarea[data-chat-input]');
                if (input) {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                        window.HTMLTextAreaElement.prototype, 'value'
                    ).set;
                    nativeInputValueSetter.call(input, data.text);
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    return (
        <iframe
            ref={iframeRef}
            srcDoc={srcdoc}
            title={title || 'Visualization'}
            sandbox="allow-scripts allow-same-origin"
            className="w-full border-0"
            style={{ height: `${iframeHeight}px`, overflow: 'hidden' }}
        />
    );
}

export default function VisualizeWidget({ artifactId }) {
    const [state, setState] = useState('loading');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const requestIdRef = useRef(0);

    const loadArtifact = async () => {
        const currentId = ++requestIdRef.current;
        setState('loading');
        setError(null);

        try {
            const artifact = await fetchArtifactUtil(artifactId);
            if (currentId !== requestIdRef.current) return;

            if (!artifact || !artifact.data) {
                throw new Error('Invalid artifact data');
            }

            const artifactData = typeof artifact.data === 'string'
                ? JSON.parse(artifact.data)
                : artifact.data;

            setData(artifactData);
            setState('loaded');
        } catch (err) {
            if (currentId !== requestIdRef.current) return;
            console.error('Failed to load visualization artifact:', err);
            setError(err.message || 'Unknown error');
            setState('error');
        }
    };

    useEffect(() => {
        if (artifactId) {
            loadArtifact();
        }
    }, [artifactId]);

    if (state === 'loading') return <VisualizeSkeleton />;
    if (state === 'error') return null;
    if (!data) return null;

    const { content_type, widget_code, title } = data;

    if (!widget_code) {
        return <VisualizeError error="No widget code in artifact" />;
    }

    if (content_type === 'svg') {
        return <SvgRenderer code={widget_code} />;
    }

    if (content_type === 'component') {
        return <ComponentRenderer data={widget_code} />;
    }

    return <HtmlRenderer code={widget_code} title={title} />;
}

export { VisualizeSkeleton, VisualizeError };
